import {
    Flex,
    Label,
    useStyles,
    Button,
    useTheme,
} from '@archipro-design/aria';
import classNames from 'classnames';
import * as S from './InviteCollaboratorForm.style';
import { useFetcher } from '@remix-run/react';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { CollaboratorAccessType } from '../../validation/schemas';
import {
    CollaboratorAccessEnumSchema,
    CollaboratorAccessEnumTitleMap,
} from '../../validation/schemas';
import { ManageDesignBoardDropdown } from './ManageDropdown';
import { FormRow } from './ManageFormRow';
import { SocialMediaShareButtons } from '~/modules/design-board/component/shared/SocialMediaShareButtons';
import { CloseLine } from '@archipro-design/icons';
import type { DesignBoardCollaborator } from '../../validation/types';
import { useDesignBoardCollaboratorsContext } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import { Pill as PillStyle } from '@modules/design-board/component/share-modal/MultiInputPills.style';
import { useDesignBoardPageState } from '@modules/design-board/component/context';
import { SHOW_COMMENT } from '@modules/design-board/config';
import type { DesignBoardWithDetailedPinsFragment } from '../../../../../generated/graphql';
import { z } from 'zod';
import { useTracker } from '@archipro-website/tracker';
import { fetcherIsDone } from '~/utils/fetcherHelper';

const EmailSeparateKeys = ['Comma', 'Space', 'Enter', 'Tab'];

interface InviteCollaboratorsFormProps {
    title?: string;
    action?: string;
    currentBoardData: DesignBoardWithDetailedPinsFragment;
    shareLink: string;
    shareTitle: string;
    shareImageUrl?: string;
    isPublic?: boolean;
    collaborators: DesignBoardCollaborator[];
}

const InviteCollaboratorsInputSchema = z
    .object({
        boardId: z.string(),
        email: z.union(
            [
                z
                    .string()
                    .trim()
                    .min(1, 'Email address is required')
                    .email({ message: 'Email address is invalid' }),
                z.array(z.string().trim().email()),
            ],
            { required_error: 'required({message: )' }
        ),
        currentEmails: z.array(z.string()).optional(),
        message: z.string().optional(),
        access: CollaboratorAccessEnumSchema,
    })
    .refine(
        (data) => {
            if (data.currentEmails === undefined) return true;
            const uniqueCurrentEmails = Array.from(new Set(data.currentEmails));
            const dataArray = [...uniqueCurrentEmails, data.email];
            const dataOverlap = new Set(dataArray);
            return dataArray.length === dataOverlap.size;
        },
        (data) => ({
            message: `${data.email} has already been invited to this board`,
            path: ['email'],
        })
    );

type InviteCollaboratorsFormShape = z.infer<
    typeof InviteCollaboratorsInputSchema
>;

export const InviteCollaboratorsForm = ({
    title,
    currentBoardData,
    shareLink,
    shareTitle,
    shareImageUrl,
    isPublic = true,
    collaborators = [],
}: InviteCollaboratorsFormProps) => {
    const { css } = useStyles();

    const fetcher = useFetcher<InviteCollaboratorsFormShape>();
    const theme = useTheme();
    const { dispatch } = useDesignBoardPageState();
    const collaboratorsContext = useDesignBoardCollaboratorsContext();
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const boardId = currentBoardData.ID;
    const tracker = useTracker();

    useEffect(() => {
        dispatch({
            type: 'SetCurrentBoardPin',
            currentBoardData,
        });
    }, [dispatch, currentBoardData]);

    const buttonBorder = `1px solid ${theme.siteVariables.colors.charcoal[250]}`;

    const permissionOptions = Object.keys(CollaboratorAccessEnumTitleMap)
        .filter((ca) => !(!SHOW_COMMENT && ca === 'comment'))
        .map((permission) => {
            return {
                render: (
                    <span>{CollaboratorAccessEnumTitleMap[permission]}</span>
                ),
                id: permission,
            };
        });

    const isLoading = fetcher.state === 'submitting';
    const currentEmails = collaborators.map((collab) => collab.email);

    const formProps = useForm<InviteCollaboratorsFormShape>({
        resolver: zodResolver(InviteCollaboratorsInputSchema),
        defaultValues: {
            boardId: boardId.toString(),
            email: '',
            access: 'view',
            message: '',
            currentEmails,
        },
        mode: 'onChange',
    });

    const [emailList, setEmailList] = useState<string[]>([]);

    const [emailInputValue, setEmailInputValue] = useState('');
    const {
        control,
        formState: { errors },
        setValue,
        handleSubmit,
        reset,
        trigger,
        setError,
    } = formProps;

    const handleFormSubmit = (data: InviteCollaboratorsFormShape) => {
        tracker.log('InviteCollaboratorsSubmit', {
            url: new URL(window.location.href),
            data: {
                ElementID:
                    'designboard_detail_header_inviteCollaboratorsReveal',
                Source: 'designBoard, detail',
            },
        });
        fetcher.submit(
            {
                email: emailList.length
                    ? emailList.join(',')
                    : typeof data.email === 'string'
                    ? data.email
                    : '',
                message: data.message || '',
                access: data.access,
                boardId: data.boardId,
                _action: 'invite_collaborators',
            },
            {
                method: 'post',
            }
        );
    };

    const cancelInvite = () => {
        collaboratorsContext.dispatch({
            type: 'modal-visibility',
            data: {
                visibility: 'hidden',
            },
        });
    };

    useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            if (Array.isArray(fetcher.data)) {
                reset();
                setValue(
                    'currentEmails',
                    fetcher.data.map((collab) => collab.email)
                );
                setEmailList([]);
            }
        }
    }, [fetcher, reset, setValue]);

    const onEmailChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
        setEmailInputValue((e.target as HTMLInputElement).value);
        setValue('email', (e.target as HTMLInputElement).value);
    };

    const onEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (EmailSeparateKeys.includes(e.code)) {
            e.preventDefault();
            trigger().then((valid) => {
                if (emailList.includes(emailInputValue)) {
                    setValue('email', '');
                    setError('email', {
                        message: 'Cannot add duplicate emails',
                    });
                } else if (valid) {
                    setEmailList([...emailList, emailInputValue]);
                    setValue('email', '');
                }
            });
        }
    };

    const removeEmailFromList = (email: string) => {
        setEmailList(emailList.filter((e) => e !== email));
    };

    useEffect(() => {
        inputContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [emailList]);

    return (
        <fetcher.Form
            className={css(S.ShareDesignBoardForm)}
            onSubmit={handleSubmit(handleFormSubmit)}
        >
            <Flex column={true} className={css(S.FormContainer)}>
                <Label
                    className={css(S.FormHeader)}
                    variant={'03'}
                    weight={'medium'}
                >
                    {title || 'Share this board'}
                </Label>

                <Controller
                    name={'email'}
                    control={control}
                    render={({ field }) => (
                        <FormRow
                            label={'Email address*'}
                            error={errors.email?.message as string}
                            field={
                                <div className={css(S.InputCol)}>
                                    {emailList.length ? (
                                        <div className={css(S.PillDivider)} />
                                    ) : null}
                                    {emailList.map((te, i) => {
                                        return (
                                            <div
                                                className={css(
                                                    PillStyle,
                                                    S.PillDesktop
                                                )}
                                                key={`key-${te}-${i}`}
                                            >
                                                <span>{te}</span>

                                                <CloseLine
                                                    onClick={() => {
                                                        removeEmailFromList(te);
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}

                                    <Flex
                                        className={classNames(
                                            css(S.EmailInput)
                                        )}
                                        ref={inputContainerRef}
                                    >
                                        <input
                                            onKeyDown={onEmailKeyDown}
                                            {...field}
                                            value={
                                                !isLoading
                                                    ? field.value || ''
                                                    : ''
                                            }
                                            onChange={onEmailChange}
                                            placeholder={'Enter email address'}
                                            className={classNames(
                                                css(S.EmailStyles)
                                            )}
                                        />
                                    </Flex>
                                </div>
                            }
                        />
                    )}
                />

                <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                        <FormRow
                            label={'Message'}
                            error={errors.message?.message as string}
                            field={
                                <Flex className={css(S.InputCol)}>
                                    <textarea
                                        {...field}
                                        placeholder={'Enter message'}
                                        className={classNames(
                                            css(S.MessageStyles)
                                        )}
                                    />
                                </Flex>
                            }
                        />
                    )}
                />

                <Controller
                    name="access"
                    control={control}
                    render={() => (
                        <FormRow
                            label={'Access'}
                            field={
                                <ManageDesignBoardDropdown
                                    defaultSelectedIndex={permissionOptions.findIndex(
                                        (opt) => opt.id === 'view'
                                    )}
                                    items={permissionOptions}
                                    onSelect={(permission) => {
                                        setValue(
                                            'access',
                                            permission as CollaboratorAccessType
                                        );
                                    }}
                                />
                            }
                        />
                    )}
                />

                {isPublic && (
                    <div className={css(S.SocialMediaShareButtonsWrapper)}>
                        <SocialMediaShareButtons
                            link={shareLink}
                            isBoard={true}
                            imageUrl={shareImageUrl}
                            title={shareTitle}
                            closeParentModal={cancelInvite}
                        />
                    </div>
                )}
                <div className={css(S.BottomButtons)}>
                    <Button
                        className={css(S.CancelButton)}
                        variant="outlined"
                        color="dark"
                        type={'button'}
                        size={16}
                        variables={{
                            darkOutlinedButtonBorderNormal: buttonBorder,
                        }}
                        onClick={() => cancelInvite()}
                        disabled={isLoading}
                    >
                        CANCEL
                    </Button>
                    <Button
                        color="dark"
                        type={'submit'}
                        size={16}
                        disabled={isLoading}
                        loading={isLoading}
                        variables={{
                            darkButtonBorderNormal: buttonBorder,
                            darkButtonBorderHover: buttonBorder,
                            darkButtonBorderSelected: buttonBorder,
                        }}
                        onClick={() => {
                            if (emailList.length) {
                                setValue('email', emailList);
                            }
                        }}
                    >
                        INVITE
                    </Button>
                </div>
            </Flex>
        </fetcher.Form>
    );
};
